import type { AlertFindingRow } from '@forecastify/shared';
import { Badge, Button, Dialog, Icon } from '@forecastify/ui';
import { useT } from '../../i18n';
import {
  alertBody,
  alertFix,
  alertImpact,
  alertName,
  areaLabel,
  goLabel,
  kindIcon,
  SEVERITY_TONE,
  penaltyOf,
  severityLabel,
  severityOf,
} from './alert-text';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C59 · one finding, in full. The mockup's `alSel*` block, which IS drawn.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **FOUR SENTENCES IN A FIXED ORDER, AND THE ORDER IS THE ARGUMENT.** What happened, what it
 * breaks, how to fix it, and then the control that takes you there. A row can only carry the
 * first; the reason this dialog exists at all is that *"the alert is a statement about a NUMBER
 * being wrong"* — and a reader who cannot see which number has a complaint, not a finding.
 *
 * ⚠️ **The severity strip takes its colour from the kind**, never from the row, because severity is
 * a property of the rule. `SEVERITY_TONE` is the mockup's own three values.
 */

export function AlertDetailDialog({
  finding,
  onClose,
  onGo,
  onDismiss,
  onRestore,
  canManage,
}: {
  readonly finding: AlertFindingRow | null;
  readonly onClose: () => void;
  readonly onGo: (finding: AlertFindingRow) => void;
  readonly onDismiss: (finding: AlertFindingRow) => void;
  readonly onRestore: (finding: AlertFindingRow) => void;
  readonly canManage: boolean;
}) {
  const t = useT();

  if (finding === null) {
    return (
      <Dialog
        open={false}
        onOpenChange={onClose}
        title=""
        width={620}
        closeLabel={t('common.close')}
      >
        <span />
      </Dialog>
    );
  }

  const severity = severityOf(finding.kind);
  const tone = SEVERITY_TONE[severity];
  const penalty = penaltyOf(finding.kind);
  const icon = kindIcon(finding.kind);
  const archived = finding.status === 'resolved';
  const waived = finding.exception !== null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={alertName(t, finding.kind)}
      width={620}
      closeLabel={t('common.close')}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {/* ⚠️ The waive lives in the footer's left corner, where `EditorDialog` puts a delete:
              it is the destructive-ish choice and should not sit beside the way out. */}
          <div>
            {!canManage ? null : waived ? (
              <Button
                variant="ghost"
                onClick={() => {
                  onRestore(finding);
                }}
              >
                {t('alerts.action.restore')}
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => {
                  onDismiss(finding);
                }}
              >
                {t('alerts.action.dismiss')}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              {t('alerts.action.close')}
            </Button>
            {/* ⚠️ Only while it is still true. A resolved finding's fix has already been made, and
                a button offering to go and make it again is an instruction to undo something. */}
            {archived ? null : (
              <Button
                onClick={() => {
                  onGo(finding);
                }}
              >
                {goLabel(t, finding.subjectType)}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-[6px] border px-2 py-[3px] text-[12px]"
            style={{
              color: tone,
              borderColor: `color-mix(in oklch, ${tone} 40%, transparent)`,
              background: `color-mix(in oklch, ${tone} 10%, transparent)`,
            }}
          >
            <Icon name="triangle-alert" size={12.5} />
            {t('alerts.detail.severity', { severity: severityLabel(t, severity) })}
          </span>
          <Badge variant="outline">{areaLabel(t, finding.kind)}</Badge>
          <span className="text-muted-foreground text-[12px] tabular-nums">
            {archived || waived
              ? t('alerts.penalty.none')
              : t('alerts.penalty.tip', { penalty: String(penalty), cap: String(penalty * 3) })}
          </span>
        </div>

        <p className="text-[13px] leading-[1.6]">{alertBody(t, finding)}</p>

        <div className="flex items-center gap-2 text-[12px]">
          <Icon name={icon} size={13} />
          <span className="text-muted-foreground">{t('alerts.detail.subject')}</span>
          <span>{finding.subjectLabel}</span>
        </div>

        <Section title={t('alerts.detail.impact')} body={alertImpact(t, finding.kind)} />
        <Section title={t('alerts.detail.fix')} body={alertFix(t, finding.kind)} />

        {!archived ? null : (
          <p className="text-muted-foreground bg-muted/40 rounded-[6px] px-3 py-2 text-[12px] leading-[1.5]">
            {t('alerts.detail.archivedNote')}
          </p>
        )}
        {finding.exception === null ? null : (
          <p className="text-muted-foreground bg-muted/40 rounded-[6px] px-3 py-2 text-[12px] leading-[1.5]">
            {t('alerts.detail.waivedNote', {
              name: finding.exception.byName,
              reason: finding.exception.reason,
            })}
          </p>
        )}

        <p className="text-muted-foreground text-[11px] tabular-nums">
          {archived && finding.resolvedAt !== null
            ? t('alerts.detail.detectedResolved', {
                date: day(finding.firstDetectedAt),
                resolved: day(finding.resolvedAt),
              })
            : t('alerts.detail.detected', { date: day(finding.firstDetectedAt) })}
        </p>
      </div>
    </Dialog>
  );
}

function Section({ title, body }: { readonly title: string; readonly body: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-[11px] tracking-[0.04em] uppercase">{title}</span>
      <p className="text-[13px] leading-[1.6]">{body}</p>
    </div>
  );
}

/**
 * ⚠️ **A timestamp cut to its date, not a formatter.** `first_detected_at` is a `timestamptz` and
 * the column shows a day; `hard-won.md`'s driver rows are about the opposite mistake — converting a
 * calendar date as if it were an instant — and this is the safe direction of the same boundary.
 */
function day(at: string): string {
  return at.slice(0, 10);
}
